
<component :is="wrapper" :title="title">
	<!--Obeserver to wrap providers-->
	<ValidationObserver
		ref="flow-form"
		:tag="'form'"
		class="flow-form"
		@submit.prevent="validateAndSubmit()"
	>
		<!--native element to wrap all field with specific direction-->
		<div
			class="flow-form-builder"
			:data-direction="direction"
			:in-accordion="accordion"
			:data-old-safari="isOldSafari()"
		>
			<!--Provider to have validation related config -->
			<ValidationProvider
				v-for="(field, idx) in computedFields"
				v-slot="{ errors, validate, passed }"
				:key="idx"
				:name="getFieldLabel(field)"
				:rules="'rules' in field ? field.rules : undefined"
				:custom-messages="'customMessages' in field ? field.customMessages : undefined"
				:vid="field.id"
				:mode="mode"
				slim
			>
				<!-- :immediate="field.immediate ? field.immediate : false" -->
				<FormField
					:inline="'inline' in field ? field.inline : false"
					:class="['flow-form-builder-' + field.type, field.cssClass]"
				>
					<!-- :style="field.style" -->

					<!--Display Field Label-->
					<template v-if="field.type !== 'accordion' && (field.label || field.info)" #label>
						<!--Label container-->
						<Container :padding="0" overflow="visible">
							<!--Label-->
							<Typography
								v-if="field.label"
								:type="field.label.type"
								:color="field.label.color"
								:weight="field.label.weight"
								:transform="field.label.transform"
								:overflow="field.label.overflow"
								:link="field.label.link"
								:data-qa-label-for="field.qaId || field.id"
								@click.native="handleTextClick(field.label)"
							>
								{{ field.label.text }}
							</Typography>

							<slot :name="field.name + '_label_actions'" />
							<!--Info with icon-->
							<!-- prettier-ignore -->
							<InfoPopover
								v-if="
									field.info &&
									typeof field.info.icon === 'object' &&
									!$slots[field.name + '_info_tootip']
								"
								container="div.flow-form-builder"
							>
								<!--Info icon-->
								<Icon
									:data-qa-info-icon-for="field.qaId || field.id"
									:name="field.info.icon.name"
									:size="field.info.icon.size"
									:type="field.info.icon.type"
									:state="field.info.icon.state"
								/>
								<template #body>
									<Container direction="column" :padding="0" :gap="8">
										<Typography
											v-if="field.info"
											:type="field.info.type"
											:color="'white'"
											:weight="field.info.weight"
											:transform="field.info.transform"
											:data-qa-info-for="field.qaId || field.id"
											:overflow="field.info.overflow"
										>
											{{ field.info.text }}
										</Typography>
									</Container>
								</template>
							</InfoPopover>
							<InfoPopover
								v-if="$slots[field.name + '_info_tootip']"
								:max-width="'50vw'"
								container="div.flow-form-builder"
							>
								<!--Info icon-->
								<Icon
									:name="'i-question-filled'"
									:size="'small'"
									:data-qa-info-icon-for="field.qaId || field.id"
									:type="'filled'"
									:state="'font-light'"
								/>
								<template #body>
									<slot
										v-if="$slots[field.name + '_info_tootip']"
										:name="field.name + '_info_tootip'"
									/>
								</template>
							</InfoPopover>
						</Container>
					</template>

					<!--Info without icon (i.e. when icon set to false)-->
					<template v-if="enableInfoSlot(field)" #info>
						<Typography
							v-if="field.info && typeof field.info.icon === 'boolean' && !field.info.icon"
							:type="field.info.type"
							:color="field.info.color"
							:weight="field.info.weight"
							:transform="field.info.transform"
							:overflow="field.info.overflow"
							:link="field.info.link"
							:data-qa-info-for="field.qaId || field.id"
							@click.native="handleTextClick(field.info)"
						>
							{{ field.info.text }}
						</Typography>
						<Container
							v-if="'flags' in field && field.flags && field.flags.length > 0"
							:gap="12"
							:padding="0"
							overflow="visible"
						>
							<template v-for="flag in field.flags">
								<Checkbox
									:key="'check-' + flag.name"
									:checked.sync="flag.value"
									@input="emitInput()"
								/>
								<Typography :key="'label-' + flag.name" type="p2">
									{{ flag.label }}
								</Typography>
							</template>
						</Container>
					</template>

					<!--Help displayed at bottom of field-->
					<template v-if="field.help || field.type === 'file' || errors" #help>
						<Container v-if="field.type === 'file'" direction="column" :padding="0" :gap="8">
							<Slab
								v-for="(file, fdx) in values[field.id]"
								:key="fdx + '_' + file.name"
								:size="field.multiple ? 'small' : 'large'"
								class="form-selected-file"
							>
								<template #primary-action>
									<Icon :effects="false" size="small" type="filled" name="i-text" />
								</template>
								<Typography type="p2">
									{{ file.name }}
								</Typography>
								<template #secondary-actions>
									<Icon
										name="i-close"
										size="x-small"
										type="filled"
										@click="removeFile(file.name, field, validate)"
									/>
								</template>
							</Slab>
						</Container>
						<slot :name="field.name + '_help'" :help="field.help">
							<Typography
								v-if="field.help"
								:type="field.help.type"
								:color="field.help.color"
								:weight="field.help.weight"
								:transform="field.help.transform"
								:overflow="field.help.overflow"
								:link="field.help.link"
								:data-qa-help-for="field.qaId || field.id"
								@click.native="handleTextClick(field.help)"
							>
								{{ field.help.text }}
							</Typography>
						</slot>
						<template v-if="errors && errors.length > 0">
							<Typography
								v-for="(error, eidx) in errors"
								:key="eidx"
								:data-qa-error-for="field.qaId || field.id"
								:type="'p2'"
								color="error"
							>
								{{ error }}
							</Typography>
						</template>
					</template>

					<!--Form field displayed dynamically-->
					<template v-if="noComponentRequired.indexOf(field.type) === -1">
						<!--Dynamic component : This is where actual Vue component rendered-->
						<component
							:is="field.component"
							v-if="checkVisibility(field)"
							:ref="field.id"
							:data-qa-id="field.qaId || field.id"
							:[field.errorProp]="checkErrorsState(field, errors, passed)"
							v-bind="field.props"
							:[field.model]="values[field.id]"
							@input="handleInput($event, field, validate)"
							@submit="validateAndSubmit()"
							@click="handleClick($event, field)"
							@initialValues="handleInitialValues($event, field)"
						>
							<template v-if="valuesInSlot.indexOf(field.type) > -1 && 'value' in field">
								{{ field.value }}
							</template>

							<!--START : Multiline selection-->

							<template v-if="field.type === 'select'" #tag="props">
								<slot
									v-if="$scopedSlots[field.name + '_tag']"
									:name="field.name + '_tag'"
									:option="props.option"
									:search="props.search"
									:remove="props.removeElement"
								/>
							</template>
							<template v-if="field.type === 'select'" #singleLabel="props">
								<slot
									v-if="$scopedSlots[field.name + '_selected_option']"
									:name="field.name + '_selected_option'"
									:option="props.option"
								/>
								<template v-if="!$scopedSlots[field.name + '_selected_option']">
									<Typography
										v-if="typeof props.option === 'string'"
										v-tooltip="props.option"
										overflow="ellipsis"
										type="p2"
										data-qa-option
										>{{ props.option }}</Typography
									>
									<Container
										v-else-if="typeof props.option === 'object' && props.option.header"
										align="space-between center"
										:gap="4"
										:grow="1"
										padding="8px 0px 8px 0px"
									>
										<Container direction="column" :grow="1" :gap="4" :padding="0">
											<Typography type="p1" data-qa-option>{{ props.option.header }}</Typography>
											<Typography type="p2" color="light">{{ props.option.details }}</Typography>
										</Container>
										<Typography type="p2" color="light">{{ props.option.meta }}</Typography>
									</Container>
									<Container
										v-else-if="typeof props.option === 'object' && props.option.icon"
										:gap="8"
										:grow="1"
										padding="8px 24px 8px 0px"
									>
										<Icon :name="props.option.icon" type="filled" size="small" :effects="false" />
										<Typography type="p2" data-qa-option>{{ props.option.label }}</Typography>
									</Container>
								</template>
							</template>
							<template v-if="field.type === 'select'" #option="props">
								<slot
									v-if="$scopedSlots[field.name + '_option']"
									:name="field.name + '_option'"
									:option="props.option"
									:search="props.search"
									:index="props.index"
								/>
								<template v-if="!$scopedSlots[field.name + '_option']">
									<Typography
										v-if="typeof props.option === 'string'"
										v-tooltip="props.option"
										type="p2"
										overflow="ellipsis"
										data-qa-option
										>{{ props.option }}</Typography
									>
									<Container
										v-else-if="typeof props.option === 'object' && props.option.header"
										align="space-between center"
										:gap="4"
										:grow="1"
										padding="8px 24px 8px 0px"
									>
										<Container direction="column" :grow="1" :gap="4" :padding="0">
											<Typography type="p1" data-qa-option>{{ props.option.header }}</Typography>
											<Typography type="p2" color="light">{{ props.option.details }}</Typography>
										</Container>
										<Typography type="p2" color="light">{{ props.option.meta }}</Typography>
									</Container>
									<Container
										v-else-if="typeof props.option === 'object' && props.option.icon"
										:gap="8"
										:grow="1"
										padding="8px 24px 8px 0px"
									>
										<Icon :name="props.option.icon" type="filled" size="small" :effects="false" />
										<Typography type="p2" data-qa-option>{{ props.option.label }}</Typography>
									</Container>
								</template>
							</template>

							<template
								v-if="field.type === 'select' && field.props.addNewOptionOnNoResult"
								#noResult="{ search }"
							>
								<Container :padding="0" :gap="8" @click.native="addOption(field, search)">
									<Typography type="p1" color="dark">{{ search }}</Typography>
									<Typography v-if="field.type === 'select'" type="p2" color="light">{{
										field.props.noResultMessage
									}}</Typography>
								</Container>
							</template>
							<!--END : Multiline selection-->
							<template
								v-if="field.type === 'radio' || field.type === 'checkbox'"
								#option_info="props"
							>
								<slot
									v-if="$scopedSlots[field.name + '_option_info']"
									:name="field.name + '_option_info'"
									:option="props.option"
								></slot>
							</template>
						</component>
					</template>
				</FormField>
			</ValidationProvider>
		</div>
	</ValidationObserver>
</component>
